import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
} from '@mui/material';
import {
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
} from '@mui/icons-material';
import { goalEarnedTotal } from '../utils/goalPoints';
import { isTournamentCampaign } from '../utils/tournamentCampaign';

const LeaderboardsSection = ({
  campaigns,
  leaderboardGoals,
  loadingLeaderboardCampaigns,
  loadingLeaderboardGoals,
  leaderboardSort,
  handleLeaderboardSortChange,
  getSortedLeaderboardGoals,
  companyExtraModules,
  highlightDealerId,
  labels,
}) => {
  const theme = useTheme();

  return (
    <Container maxWidth="lg" sx={{ py: 4, pl: 0.5, pr: 4 }}>
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#ffffff' }}>
        <Typography variant="h5" gutterBottom>
          {labels.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {labels.description}
        </Typography>

        {loadingLeaderboardCampaigns ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : campaigns.length === 0 ? (
          <Alert severity="info">
            {labels.noLeaderboards}
          </Alert>
        ) : loadingLeaderboardGoals ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          campaigns.map((campaign) => {
            const goals = leaderboardGoals[campaign.id] || [];
            const sortedGoals = getSortedLeaderboardGoals(goals);
            const tournamentCampaign = isTournamentCampaign(campaign, companyExtraModules);
            return (
              <Box key={campaign.id} sx={{ mb: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  {campaign.name}
                </Typography>
                {sortedGoals.length > 0 ? (
                  <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff', mb: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          {tournamentCampaign && (
                            <TableCell align="center" sx={{ width: 72 }}>
                              {labels.rank || 'Rank'}
                            </TableCell>
                          )}
                          <TableCell
                            onClick={() => handleLeaderboardSortChange('dealer')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {tournamentCampaign ? labels.teamName : labels.dealerName}
                              {leaderboardSort.column === 'dealer' && (
                                leaderboardSort.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={() => handleLeaderboardSortChange('points')}
                            sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                              {labels.points}
                              {leaderboardSort.column === 'points' && (
                                leaderboardSort.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                              )}
                            </Box>
                          </TableCell>
                          {!tournamentCampaign && campaign.show_value_1 && (
                            <TableCell
                              align="right"
                              onClick={() => handleLeaderboardSortChange('value_1')}
                              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {campaign.value_1_label || 'Value 1'}
                                {leaderboardSort.column === 'value_1' && (
                                  leaderboardSort.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                )}
                              </Box>
                            </TableCell>
                          )}
                          {!tournamentCampaign && campaign.show_value_2 && (
                            <TableCell
                              align="right"
                              onClick={() => handleLeaderboardSortChange('value_2')}
                              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {campaign.value_2_label || 'Value 2'}
                                {leaderboardSort.column === 'value_2' && (
                                  leaderboardSort.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                )}
                              </Box>
                            </TableCell>
                          )}
                          {!tournamentCampaign && campaign.show_value_3 && (
                            <TableCell
                              align="right"
                              onClick={() => handleLeaderboardSortChange('value_3')}
                              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                {campaign.value_3_label || 'Value 3'}
                                {leaderboardSort.column === 'value_3' && (
                                  leaderboardSort.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                )}
                              </Box>
                            </TableCell>
                          )}
                          {campaign.show_status && (
                            <TableCell
                              align="center"
                              onClick={() => handleLeaderboardSortChange('status')}
                              sx={{ cursor: 'pointer', '&:hover': { backgroundColor: '#f5f5f5' } }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {labels.status}
                                {leaderboardSort.column === 'status' && (
                                  leaderboardSort.direction === 'asc' ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />
                                )}
                              </Box>
                            </TableCell>
                          )}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedGoals.map((goal, index) => (
                          <TableRow
                            key={goal.id}
                            sx={goal.dealer_id === highlightDealerId ? { backgroundColor: theme.palette.mode === 'light' ? 'rgba(25, 118, 210, 0.08)' : 'rgba(25, 118, 210, 0.2)' } : {}}
                          >
                            {tournamentCampaign && (
                              <TableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {index + 1}
                                </Typography>
                              </TableCell>
                            )}
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {goal.dealer
                                  ? (goal.dealer.tournament_nickname || goal.dealer.name)
                                  : labels.unknownDealer}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={goalEarnedTotal(goal)} size="small" color="primary" variant="outlined" />
                            </TableCell>
                            {!tournamentCampaign && campaign.show_value_1 && (
                              <TableCell align="right">{goal.value_1 ?? 0}</TableCell>
                            )}
                            {!tournamentCampaign && campaign.show_value_2 && (
                              <TableCell align="right">{goal.value_2 ?? 0}</TableCell>
                            )}
                            {!tournamentCampaign && campaign.show_value_3 && (
                              <TableCell align="right">{goal.value_3 ?? 0}</TableCell>
                            )}
                            {campaign.show_status && (
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Chip label="1" size="small" color={goal.done_1 ? 'success' : 'default'} variant={goal.done_1 ? 'filled' : 'outlined'} />
                                  <Chip label="2" size="small" color={goal.done_2 ? 'success' : 'default'} variant={goal.done_2 ? 'filled' : 'outlined'} />
                                  <Chip label="3" size="small" color={goal.done_3 ? 'success' : 'default'} variant={goal.done_3 ? 'filled' : 'outlined'} />
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">{labels.noPerformanceData}</Alert>
                )}
              </Box>
            );
          })
        )}
      </Paper>
    </Container>
  );
};

export default LeaderboardsSection;
